import { KeycloakInitOptions } from 'keycloak-js';

const originalPathStorageKey = 'keycloakServerLoginFlow.originalPath';
const resultVerifierStorageKey = 'keycloakServerLoginFlow.resultVerifier';
const defaultAuthenticatedPath = '/devices';
export const keycloakServerLoginCallbackPath = '/auth/keycloak/callback';

export interface KeycloakServerLoginTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
}

export type KeycloakServerLoginFlow = 'jwt' | 'session';

export type KeycloakServerLoginCallbackResult =
  | { kind: 'jwt'; loginResult: string }
  | { kind: 'session' }
  | { kind: 'missing' };

type BrowserLocation = Pick<
  Location,
  'origin' | 'pathname' | 'search' | 'hash'
>;

export function buildServerLoginUrl(
  restUrl: string,
  returnUrl: string,
  realm: string,
  resultChallenge: string,
): string {
  const loginUrl = new URL(`${restUrl.replace(/\/$/, '')}/auth/keycloak/login`);
  loginUrl.searchParams.set('returnUrl', returnUrl);
  loginUrl.searchParams.set('realm', realm);
  loginUrl.searchParams.set('resultChallenge', resultChallenge);
  return loginUrl.toString();
}

/**
 * PKCE S256 for the loginResult exchange (jwt flow): login-start gets
 * BASE64URL(SHA256(verifier)), the redeem sends the verifier. The server hands
 * a loginResult only to the tab holding the verifier, so a ?loginResult link
 * someone else sends (their own login, stopped before redeeming) cannot sign
 * this browser in as them. The session flow ignores it.
 */
export async function createResultChallenge(storage: Storage): Promise<string> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  storage.setItem(resultVerifierStorageKey, verifier);
  const hash = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(hash));
}

/** One use: a login result is spent on its first redeem, right or wrong. */
export function consumeResultVerifier(storage: Storage): string | null {
  const verifier = storage.getItem(resultVerifierStorageKey);
  storage.removeItem(resultVerifierStorageKey);
  return verifier;
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function buildCallbackReturnUrl(
  location: Pick<BrowserLocation, 'origin'>,
): string {
  return new URL(keycloakServerLoginCallbackPath, location.origin).toString();
}

export function getCurrentSpaPath(location: BrowserLocation): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function getLoginResult(search: string): string | null {
  return new URLSearchParams(search).get('loginResult');
}

export function getSessionLoginResult(search: string): string | null {
  return new URLSearchParams(search).get('sessionLogin');
}

export function getLoginError(search: string): string | null {
  return new URLSearchParams(search).get('error');
}

export function getServerLoginCallbackResult(
  search: string,
  loginFlow: KeycloakServerLoginFlow,
): KeycloakServerLoginCallbackResult {
  if (loginFlow === 'jwt') {
    const loginResult = getLoginResult(search);

    return loginResult ? { kind: 'jwt', loginResult } : { kind: 'missing' };
  }

  return getSessionLoginResult(search) === 'success'
    ? { kind: 'session' }
    : { kind: 'missing' };
}

export function isInvalidStateLoginError(search: string): boolean {
  return getLoginError(search) === 'invalidState';
}

export function isServerLoginCallback(
  location: Pick<BrowserLocation, 'pathname'>,
): boolean {
  return location.pathname === keycloakServerLoginCallbackPath;
}

export async function prepareServerLoginRedirect(input: {
  restUrl: string;
  realm: string;
  location: BrowserLocation;
  storage: Storage;
}): Promise<string> {
  const { restUrl, realm, location, storage } = input;

  if (!isServerLoginCallback(location)) {
    storage.setItem(originalPathStorageKey, getCurrentSpaPath(location));
  }

  return buildServerLoginUrl(
    restUrl,
    buildCallbackReturnUrl(location),
    realm,
    await createResultChallenge(storage),
  );
}

export function consumeOriginalPath(storage: Storage): string {
  const originalPath =
    storage.getItem(originalPathStorageKey) || defaultAuthenticatedPath;

  storage.removeItem(originalPathStorageKey);

  return originalPath;
}

export function toKeycloakInitOptions(
  tokenResponse: KeycloakServerLoginTokenResponse,
): KeycloakInitOptions {
  return {
    token: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token,
    idToken: tokenResponse.id_token,
    checkLoginIframe: false,
  };
}
